import { Link } from "react-router-dom";
import { CheckCircle2, FileSignature } from "lucide-react";
import { useTranslation } from "react-i18next";
import { money } from "@/lib/format";

const Row = ({ k, v, green }) => (
  <div className="flex justify-between py-3 border-b border-slate-200"><span className="text-slate-500">{k}</span><span className={`font-medium ${green ? "text-green-600" : "text-slate-900"}`}>{v}</span></div>
);

// Post-payment confirmation screen shown when a booking is paid + confirmed.
export default function ConfirmationScreen({ confirmed }) {
  const { t } = useTranslation();
  return (
    <div data-testid="booking-confirmation" className="bg-white min-h-screen pt-32 pb-24 px-5 sm:px-8">
      <div className="max-w-2xl mx-auto text-center">
        <span className="grid place-items-center w-16 h-16 rounded-full bg-green-100 text-green-600 mx-auto"><CheckCircle2 size={36} /></span>
        <h1 className="font-heading text-4xl text-slate-900 mt-7 tracking-tight font-bold">{t("confirm.title")}</h1>
        <p className="text-slate-600 mt-4">{t("confirm.thankYou", { name: confirmed.customer_name, amount: money(confirmed.amount_due), phone: confirmed.customer_phone })}</p>

        <div className="bg-slate-50 rounded-2xl mt-9 p-8 text-left">
          <Row k={t("confirm.vehicle")} v={confirmed.car_name} />
          <Row k={t("confirm.pickupLocation")} v={confirmed.pickup_location} />
          <Row k={t("confirm.pickup")} v={confirmed.pickup_date} />
          <Row k={t("confirm.return")} v={confirmed.return_date} />
          <Row k={t("confirm.duration")} v={t("common.days", { count: confirmed.days })} />
          <Row k={t("confirm.mileage")} v={confirmed.unlimited_miles ? t("confirm.unlimited") : t("confirm.milesIncluded", { miles: (confirmed.included_miles || 0).toLocaleString() })} />
          <Row k={!confirmed.refundable ? t("confirm.rentalNonRef") : t("confirm.rental")} v={money(confirmed.rental)} />
          {confirmed.service_fee > 0 && <Row k={t("confirm.serviceFee", { pct: Math.round((confirmed.service_fee_rate || 0.1) * 100) })} v={money(confirmed.service_fee)} />}
          <Row k={t("confirm.tax")} v={money(confirmed.tax)} />
          {confirmed.young_driver_fee > 0 && <Row k={t("confirm.youngDriverFee")} v={money(confirmed.young_driver_fee)} />}
          {confirmed.airport_dropoff && <Row k={t("confirm.airportRow")} v={money(confirmed.airport_fee)} />}
          {confirmed.unlimited_miles && <Row k={t("confirm.unlimitedMiles")} v={money(confirmed.unlimited_miles_fee)} />}
          {confirmed.discount_amount > 0 && <Row k={t("confirm.discount", { code: confirmed.discount_code })} v={`−${money(confirmed.discount_amount)}`} green />}
          <div className="flex justify-between py-3 border-b border-slate-200">
            <span className="text-slate-500">{t("confirm.refundableDeposit")}</span>
            <span className="text-green-600 font-medium flex items-center gap-1.5"><CheckCircle2 size={15} /> {money(confirmed.deposit)} {t("confirm.paid")}</span>
          </div>
          <div className="flex justify-between pt-4"><span className="text-slate-600 font-medium">{t("confirm.totalPaid")}</span><span data-testid="confirm-total-paid" className="font-heading text-2xl text-slate-900 font-bold">{money(confirmed.amount_due)}</span></div>
        </div>

        {confirmed.signature_name && (
          <p data-testid="confirm-agreement" className="text-slate-400 text-xs mt-5 flex items-center justify-center gap-1.5"><FileSignature size={13} /> {t("confirm.agreementSigned", { name: confirmed.signature_name })}</p>
        )}

        <div className="flex gap-4 justify-center mt-9">
          <Link to="/fleet" data-testid="confirm-back-fleet" className="border-2 border-slate-200 text-slate-700 px-8 py-3.5 rounded-full font-semibold hover:bg-slate-50 transition-colors">{t("confirm.browseMore")}</Link>
          <Link to="/" className="bg-blue-600 text-white px-8 py-3.5 rounded-full font-semibold hover:bg-blue-700 transition-colors">{t("confirm.home")}</Link>
        </div>
      </div>
    </div>
  );
}
